import { buildIdentityBadges } from "./identityBadges.js";

const defaultTopVotersPerOption = 10;
const defaultRecentActivityLimit = 100;

export function buildMarketSocialProof({
  market,
  options,
  votes,
  identityVerifications = new Map(),
  walletXIdentities = new Map(),
  snsXIdentities = new Map(),
  topVotersPerOption = defaultTopVotersPerOption,
  recentActivityLimit = defaultRecentActivityLimit
}) {
  const safeOptions = Array.isArray(options) ? options : [];
  const safeVotes = Array.isArray(votes) ? votes : [];
  const optionById = new Map(safeOptions.map((option) => [optionId(option), option]));
  const buckets = new Map(safeOptions.map((option) => [optionId(option), new Map()]));
  const normalizedVotes = safeVotes
    .map((vote) => normalizeVote(vote, optionById))
    .filter(Boolean);

  for (const vote of normalizedVotes) {
    const userTotals = buckets.get(vote.optionId);
    userTotals.set(vote.walletAddress, (userTotals.get(vote.walletAddress) || 0) + vote.voteAmountCents);
  }

  const optionTopVoters = safeOptions.map((option) => {
    const currentOptionId = optionId(option);
    const voters = Array.from((buckets.get(currentOptionId) || new Map()).entries())
      .map(([walletAddress, totalCents]) => ({
        walletAddress,
        ...displayIdentityFor(walletAddress, snsXIdentities, { identityVerifications, walletXIdentities }),
        totalVotes: fromCents(totalCents)
      }))
      .sort((left, right) => right.totalVotes - left.totalVotes || left.walletAddress.localeCompare(right.walletAddress))
      .slice(0, topVotersPerOption);

    return {
      optionId: currentOptionId,
      optionLabel: optionLabel(option),
      voters
    };
  });

  const recentActivity = normalizedVotes
    .slice()
    .sort((left, right) => right.createdAtMs - left.createdAtMs)
    .slice(0, recentActivityLimit)
    .map((vote) => {
      const currentOption = optionById.get(vote.optionId);
      return {
        id: vote.id,
        walletAddress: vote.walletAddress,
        ...displayIdentityFor(vote.walletAddress, snsXIdentities, { identityVerifications, walletXIdentities }),
        optionId: vote.optionId,
        optionLabel: optionLabel(currentOption),
        voteAmount: fromCents(vote.voteAmountCents),
        createdAt: vote.createdAt
      };
    });

  return {
    marketId: market?.id || market?.marketId || "",
    optionTopVoters,
    recentActivity
  };
}

export function displayIdentityFor(
  walletAddress,
  snsXIdentities = new Map(),
  { identityVerifications = new Map(), walletXIdentities = new Map(), nickname = null } = {}
) {
  const badgeFields = identityBadgeFields(identityVerifications.get(walletAddress));
  const linkedIdentity = walletXIdentities.get(walletAddress);
  if (linkedIdentity?.linkStatus === "linked" && linkedIdentity?.xHandle) {
    return {
      displayName: `@${linkedIdentity.xHandle}`,
      displayKind: "linked_x",
      xUserId: linkedIdentity.xUserId || null,
      xHandle: linkedIdentity.xHandle,
      ...badgeFields
    };
  }

  const identity = snsXIdentities.get(walletAddress);
  if (identity?.lookupStatus === "found" && identity?.verifiedOwner && identity?.xHandle) {
    return {
      displayName: `@${identity.xHandle}`,
      displayKind: "sns_x",
      xUserId: null,
      xHandle: identity.xHandle,
      ...badgeFields
    };
  }

  const safeNickname = typeof nickname === "string" ? nickname.trim() : "";
  if (safeNickname) {
    return {
      displayName: safeNickname,
      displayKind: "nickname",
      xUserId: null,
      xHandle: null,
      ...badgeFields
    };
  }

  return {
    displayName: null,
    displayKind: "wallet",
    xUserId: null,
    xHandle: null,
    ...badgeFields
  };
}

function identityBadgeFields(verification) {
  const identityBadges = buildIdentityBadges(verification);
  return identityBadges.length ? { identityBadges } : {};
}

function optionId(option) {
  return option?.id || option?.optionId || option?.option_id || "";
}

function optionLabel(option) {
  return option?.label || option?.optionLabel || option?.option_label || optionId(option);
}

function voteId(vote) {
  return vote?.id || vote?.voteId || "";
}

function voteOptionId(vote) {
  return vote?.option_id || vote?.optionId || "";
}

function voteUserId(vote) {
  return vote?.user_id || vote?.userId || vote?.user_wallet || vote?.userWallet || "";
}

function voteAmount(vote) {
  return vote?.vote_amount ?? vote?.voteAmount ?? 0;
}

function voteCreatedAt(vote) {
  return vote?.created_at || vote?.createdAt || "";
}

function normalizeVote(vote, optionById) {
  const currentOptionId = voteOptionId(vote);
  if (!currentOptionId || !optionById.has(currentOptionId)) return null;

  const walletAddress = voteUserId(vote);
  if (!walletAddress) return null;

  const amount = Number(voteAmount(vote));
  if (!Number.isFinite(amount)) return null;
  const voteAmountCents = toCents(amount);
  if (voteAmountCents <= 0) return null;

  const createdAt = voteCreatedAt(vote);
  const createdAtMs = new Date(createdAt).getTime();
  if (!Number.isFinite(createdAtMs)) return null;

  return {
    id: voteId(vote),
    optionId: currentOptionId,
    walletAddress,
    voteAmountCents,
    createdAt,
    createdAtMs
  };
}

function toCents(value) {
  return Math.round(Number(value) * 100);
}

function fromCents(value) {
  return Number((Number(value || 0) / 100).toFixed(2));
}
